import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Input, Message } from 'semantic-ui-react';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledAddButton } from 'common/components/button/StyledButton';
import { surveyQuestionApi } from '../api/surveyQuestionApi';
import { useNavigate } from 'react-router';
import { QuestionStatus, QuestionTypes } from '../constants/SurveyQuestionTypes';
import SurveyQuestionList from './SurveyQuestionList';
import LoadingSpinner from '@/common/components/LoadingSpinner';

const surveyTypeOptions = [
  { key: 1, text: 'Pillars', value: QuestionTypes.PILLARS },
  { key: 2, text: 'Outcomes', value: QuestionTypes.OUTCOMES },
];

const SurveyQuestionManagement: React.FC = () => {
  const [surveyTypeFilter, setSurveyTypeFilter] = useState<number | undefined>(QuestionTypes.OUTCOMES);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const { data: questions = [], isLoading, error, refetch } =
    surveyQuestionApi.useApiAdminV1SurveyQuestionListQuery({ search, surveyType: surveyTypeFilter });

  const filtered = useMemo(
    () =>
      questions.filter((q) => {
        if (statusFilter === 'active') return q.status === QuestionStatus.ACTIVE;
        if (statusFilter === 'archived') return q.status === QuestionStatus.ARCHIVED;
        return true; // 'all'
      }),
    [questions, statusFilter]
  );

  useEffect(() => setCurrentPage(1), [surveyTypeFilter, statusFilter, search]);

  const title =
    surveyTypeFilter === QuestionTypes.PILLARS ? 'Pillar Questions' : 'Outcome Questions';

  return (
    <PageContent>
      <PageSection>
        <PageHeader
          title={title}
          divider
          controls={
            <StyledAddButton
              label={surveyTypeFilter === QuestionTypes.PILLARS ? 'Create Pillar Questions' : 'Create Outcome Questions'}
              width="200px"
              height="34px"
              onClick={() =>
                navigate('create', { state: { surveyType: surveyTypeFilter ?? QuestionTypes.OUTCOMES } })
              }
            />
          }
        />

        <PageSection display="flex" justifyContent="space-between" alignItems="center">
          <PageSection>
            <Dropdown
              placeholder="Filter by Question Type"
              selection
              value={surveyTypeFilter}
              options={surveyTypeOptions}
              onChange={(_, { value }) => setSurveyTypeFilter(value as number | undefined)}
              style={{
                height: '36px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '14px',
                minWidth: '220px',
              }}
            />
            <Input
              placeholder="Search by Name"
              value={search}
              onChange={(_, { value }) => setSearch(value)}
              style={{
                height: '36px',
                marginLeft: '12px',
              }}
            />
          </PageSection>

          <Dropdown
            placeholder="Filter by Status"
            selection
            value={statusFilter}
            options={[
              { key: 'all', text: 'All Statuses', value: 'all' },
              { key: 'active', text: 'Active', value: 'active' },
              { key: 'archived', text: 'Archived', value: 'archived' },
            ]}
            onChange={(_, { value }) => setStatusFilter(value as typeof statusFilter)}
            style={{
              height: '36px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontSize: '14px',
              minWidth: '200px',
              marginLeft: '12px',
            }}
          />
        </PageSection>

        {error && <Message negative content="Failed to load survey questions." />}
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <SurveyQuestionList
            questions={filtered}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onNavigate={navigate}
            refetch={refetch}
          />
        )}
      </PageSection>
    </PageContent>
  );
};

export default SurveyQuestionManagement;
